import React from "react";
import axios from "axios";
import * as HttpStatus from "http-status-codes";
import { Box, Typography, TextField, Button, Tooltip } from "@mui/material";
import { LogConfigurationModel } from "acr-common-models";
import { translate } from "../../i18n";
import { CustomDialog } from "components/CustomDialog";
import { addNotification } from "utils/NotificationHandler";
import { object, number } from "yup";
import { acrAPI } from "utils/api";
import { Formik, Form, Field, FormikTouched } from "formik";
import { Subscription } from "rxjs";
import { Download } from "components/Logs/Header/Download";

export class PurgeLogs extends React.Component {
  private subscriptions: Subscription = new Subscription();

  private validationSchema = object({
    logRetentionSnap: number()
      .required(translate("Number of days to retain log dumps"))
      .min(7, translate("Value must be greater or equal to 7"))
      .max(365, translate("Value must be lower or equal to 365")),
    logRetentionInk: number()
      .required(translate("Number of days to retain log dumps"))
      .min(7, translate("Value must be greater or equal to 7"))
      .max(365, translate("Value must be lower or equal to 365")),
    logRetentionSoftware: number()
      .required(translate("Number of days to retain log dumps"))
      .min(7, translate("Value must be greater or equal to 7"))
      .max(365, translate("Value must be lower or equal to 365")),
  });

  public state = { logRetentionSnap: 31,logRetentionSoftware: 31,logRetentionInk: 31, logsToPurge: null, dialogOpen: false };
  constructor(props: any) {
    super(props);
    this.updateLogConfiguration = this.updateLogConfiguration.bind(this);
  }

  public componentDidMount(): void {
    this.setState({ logRetentionSnap: 31,logRetentionSoftware: 31, logRetentionInk: 31, dialogOpen: false });
    this.subscriptions.add(acrAPI.logConfigurationSubject.subscribe(this.updateLogConfiguration));
    acrAPI.getLogConfiguration();
  }
  public componentWillUnmount(): void {
    this.subscriptions.unsubscribe();
  }
  private closePurgeLogsConfirmation(): void {
    this.setState({ dialogOpen: false });
  }
   private updateLogConfiguration(logConfigurationModel: LogConfigurationModel): void {
    this.setState({ ...logConfigurationModel });
  }

  openPurgeSnapLogsConfirmation(): void {
    this.setState({ logsToPurge: "snap", dialogOpen: true });
  }

  openPurgeInkLogsConfirmation(): void {
    this.setState({ logsToPurge: "ink", dialogOpen: true });
  }

  openPurgeSoftwareLogsConfirmation(): void {
    this.setState({ logsToPurge: "software", dialogOpen: true });
  }

  private async purgeLogs(): Promise<void> {
    const apiPath = "/maintenance/purgeLogs/"+this.state.logsToPurge;
    axios.get(apiPath).then((res) => {
      this.setState({ logsToPurge: null, dialogOpen: false });
      if (res.status === HttpStatus.OK) {
        addNotification(translate("Logs purge successful"));
      } else {
        addNotification(translate("Failed to purge log"), "error");
      }
    });
  }

  private async handleSubmit(values: any): Promise<void> {
    await acrAPI.updateLogConfiguration(values);
  }

  private canSubmit(errors: any, touched: FormikTouched<LogConfigurationModel>): boolean {
    return Object.keys(errors).length === 0 && Object.keys(touched).length > 0;
  }
  public render(): JSX.Element {
    return (
      <>

        {/* DialogBox for purge confiramtion */}
        <CustomDialog
          title={translate("Delete confirmation logs title "+ this.state.logsToPurge) }
          message={translate("Delete confirmation logs text "+ this.state.logsToPurge)}
          action={() => this.purgeLogs()}
          open={this.state.dialogOpen}
          disableAction={false}
          handleClose={() => this.closePurgeLogsConfirmation()}
        />
        <Box margin={2} p={2} display="flex" flexDirection="column">
        <Formik
          initialValues={this.state}
          enableReinitialize={true}
          validationSchema={this.validationSchema}
          onSubmit={async (values, { resetForm }) => {
            await this.handleSubmit(values);
            resetForm({ values });
          }}>
          {({ handleSubmit, handleChange, setFieldTouched, touched, values, errors }) => (
            <Form name="settings-snmp-configuration-form" translate="no" method="POST" onSubmit={handleSubmit}>

          {/* FORM */}
          <Box display="flex" flexDirection="row">

            {/* SNAP LOGS*/}
            <Box display="flex" flexDirection="column" width={"20%"} margin={"1em"}>
              <Box>
                <Typography variant="h5" data-cy="maintenance-purge-snap-logs-title">
                  {translate("SNAP logs")}
                </Typography>
              </Box>
              <Box>
              <Field
                    component={TextField}
                    variant="outlined"
                    margin="normal"
                    id="logRetentionSnap"
                    name="logRetentionSnap"
                    label={translate("period of retention (days)")}
                    fullWidth
                    helperText={errors.logRetentionSnap}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setFieldTouched("logRetentionSnap", true);
                      handleChange(e);
                    }}
                    value={values.logRetentionSnap}
                    data-cy={`snmp-community-name`}
                  />
              </Box>
              <Tooltip title={translate("download watermarking logs")}>
                  <Box style={{ margin: "1em" }} width={"100%"}>
                    <Download
                      checkCount={true}
                      logType={"maintenance/log/snap"} />
                  </Box>
              </Tooltip>
              <Tooltip title={translate("purge logs tooltip")}>
              <Box style={{ margin: "1em" }} width={"100%"}>
                <Button
                  type="button"
                  data-cy="maintenance-purge-snap-logs-button"
                  variant="contained"
                  color="primary"
                  onClick={() => this.openPurgeSnapLogsConfirmation()}>
                  {translate("purge")}
                </Button>
                </Box>
              </Tooltip>
            </Box>

            {/* INK LOGS*/}
            <Box display="flex" flexDirection="column" width={"20%"} margin={"1em"}>
              <Box>
                <Typography variant="h5" data-cy="maintenance-purge-ink-logs-title">
                  {translate("INK logs")}
                </Typography>
              </Box>
              <Box>
              <Field
                    component={TextField}
                    variant="outlined"
                    margin="normal"
                    id="logRetentionInk"
                    name="logRetentionInk"
                    label={translate("period of retention (days)")}
                    fullWidth
                    helperText={errors.logRetentionInk}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                      setFieldTouched("logRetentionInk", true);
                      handleChange(e);
                    }}
                    value={values.logRetentionInk}
                    data-cy={`snmp-community-name`}
                  />
              </Box>
              <Tooltip title={translate("download watermarking logs")}>
                  <Box style={{ margin: "1em" }} width={"100%"}>
                    <Download
                      checkCount={true}
                      logType={"maintenance/log/ink"} />
                  </Box>
              </Tooltip>
              <Tooltip title={translate("purge logs tooltip")}>
              <Box style={{ margin: "1em" }} width={"100%"}>
                <Button
                  type="button"
                  data-cy="maintenance-purge-ink-logs-button"
                  variant="contained"
                  color="primary"
                  onClick={() => this.openPurgeInkLogsConfirmation()}>
                  {translate("purge")}
                </Button>
                </Box>
              </Tooltip>
            </Box>

          {/* SOFTWARE LOGS*/}
          <Box display="flex" flexDirection="column" width={"20%"} margin={"1em"}>
              <Box>
                <Typography variant="h5" data-cy="maintenance-purge-software-logs-title">
                  {translate("Software logs")}
                </Typography>
              </Box>
              <Box>
                  <Field
                    component={TextField}
                    variant="outlined"
                    margin="normal"
                    id="logRetentionSoftware"
                    name="logRetentionSoftware"
                    label={translate("period of retention (days)")}
                    fullWidth
                    helperText={errors.logRetentionSoftware}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    setFieldTouched("logRetentionSoftware", true);
                    handleChange(e);
                  }}
                  value={values.logRetentionSoftware}
                  data-cy={`snmp-community-name`}
                />
              </Box>
              <Tooltip title={translate("download software logs")}>
                  <Box style={{ margin: "1em" }} width={"100%"}>
                    <Download
                      checkCount={true}
                      logType={"maintenance/log/software"} />
                  </Box>
              </Tooltip>
              <Tooltip title={translate("purge logs tooltip")}>
              <Box style={{ margin: "1em" }}>
                <Button
                  type="button"
                  data-cy="maintenance-purge-snap-logs-button"
                  variant="contained"
                  color="primary"
                  onClick={() => this.openPurgeSoftwareLogsConfirmation()}>
                  {translate("purge")}
                </Button>
                </Box>
              </Tooltip>
            </Box>
            </Box>

          <Box width={"20%"}>
          <Button
                  type="submit"
                  disabled={!this.canSubmit(errors, touched)}
                  variant="contained"
                  data-cy="snmp-settings-configuration-save-button"
                  style={{ margin: "1.25em 0 1.5em 0em", height: "2.5em", padding: "0 2em" }}
                  color="primary">
                  {translate("save")}
                </Button>
          </Box>

          {/* END FORM */}
          </Form>
          )}
        </Formik>
        </Box>
      </>
      
    );
  }
}